import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { jobsAPI, sponsorshipsAPI } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Check, ArrowLeft, Crown } from 'lucide-react';

export default function SelectSponsorship() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selecting, setSelecting] = useState(false);

  // 3 Pacotes baseados em quantidade de vagas
  const plans = [
    {
      id: 'basico',
      name: 'Básico',
      subtitle: '1 Vaga',
      price_cve: 0,
      price_eur: 0,
      price_after_trial_cve: 500,
      price_after_trial_eur: 4.5,
      is_free_trial: true,
      trial_days: 30,
      max_jobs: 1,
      features: [
        '🎉 GRÁTIS por 30 dias!',
        'Depois apenas 500 CVE/mês',
        '1 vaga ativa por mês',
        'Visibilidade padrão',
        'Aparece nos resultados de pesquisa',
        'Renovação mensal automática',
        'Suporte por email'
      ],
      recommended: false,
      color: 'green'
    },
    {
      id: 'standard',
      name: 'Standard',
      subtitle: 'Até 5 Vagas',
      price_cve: 2500,
      price_eur: 23,
      max_jobs: 5,
      features: [
        'Até 5 vagas ativas por mês',
        'Visibilidade aumentada',
        'Badge "Empregador Verificado"',
        'Prioridade nos resultados',
        'Analytics detalhados',
        'Suporte prioritário',
        'Recomendado para PMEs'
      ],
      recommended: true,
      color: 'blue'
    },
    {
      id: 'ilimitado',
      name: 'Ilimitado',
      subtitle: 'Vagas Ilimitadas',
      price_cve: 6000,
      price_eur: 55,
      max_jobs: 9999,
      features: [
        'Vagas ilimitadas por mês',
        'Máxima visibilidade',
        'Badge "Empregador Premium"',
        'Top posições garantidas',
        'Analytics avançados em tempo real',
        'Gestor de conta dedicado',
        'Suporte 24/7',
        'Promoção nas redes sociais',
        'Ideal para grandes empresas'
      ],
      recommended: false,
      color: 'purple'
    }
  ];

  useEffect(() => {
    loadJob();
  }, [jobId]);

  const loadJob = async () => {
    try {
      const { data } = await jobsAPI.getById(jobId);
      setJob(data);
    } catch (error) {
      setMessage('❌ Vaga não encontrada');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    setSelecting(true);
    setMessage('');

    try {
      // Create sponsorship
      await sponsorshipsAPI.create({
        job_id: jobId,
        plan_type: plan.id
      });

      // Publish job
      await jobsAPI.publish(jobId);

      setMessage('✅ Plano selecionado! Redirecionando...');
      setTimeout(() => navigate('/my-jobs'), 2000);
    } catch (error) {
      setMessage('❌ Erro ao selecionar plano');
      setSelecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/my-jobs')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Escolha o Seu Plano
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Selecione o plano que melhor se adequa às necessidades da sua empresa
          </p>
          {job && (
            <p className="text-sm text-gray-500 mt-2">
              Vaga: <strong>{job.title_pt}</strong>
            </p>
          )}
        </div>

        {message && (
          <Alert className={message.includes('✅') ? 'border-green-500 bg-green-50 mb-6' : 'border-red-500 bg-red-50 mb-6'}>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative p-8 ${plan.recommended ? 'ring-2 ring-blue-500 shadow-xl scale-105' : 'hover:shadow-lg'} transition-all`}
            >
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg flex items-center gap-1">
                    <Crown className="h-4 w-4" />
                    Mais Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                <p className="text-gray-600 font-medium">{plan.subtitle}</p>
              </div>

              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-4xl font-bold text-gray-900">
                    {plan.price_cve.toLocaleString()}
                  </span>
                  <span className="text-gray-600">CVE</span>
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  ~{plan.price_eur}€ / mês
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleSelectPlan(plan)}
                disabled={selecting}
                className={`w-full ${
                  plan.recommended
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                    : 'bg-gray-900 hover:bg-gray-800'
                }`}
              >
                {selecting ? 'A processar...' : 'Selecionar Plano'}
              </Button>
            </Card>
          ))}
        </div>

        <div className="mt-12 max-w-3xl mx-auto">
          <Card className="p-6 bg-blue-50 border-blue-200">
            <h3 className="font-semibold text-gray-900 mb-3">💡 Como Funciona</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Planos são cobrados mensalmente</li>
              <li>• Pode cancelar ou mudar de plano a qualquer momento</li>
              <li>• Vagas permanecem ativas enquanto o plano estiver ativo</li>
              <li>• Receberá candidaturas diretamente no seu painel</li>
              <li>• Pagamento será configurado com sua conta bancária de Cabo Verde</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
